import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import {
  disciplineApi,
  drawingTypeApi,
  issueCategoryApi,
  labourCategoryApi,
  materialApi,
  materialCategoryApi,
  uomApi,
  useAllDisciplines,
  useAllDrawingTypes,
  useAllIssueCategories,
  useCreateParty,
  useDeleteParty,
  useParties,
  useUpdateParty,
  workPackageApi,
  type PartySaveRequest,
  type PartyType,
} from '../api/admin'
import {
  useLabourCategories,
  useMaterialCategories,
  useMaterials,
  useUoms,
  useWorkPackages,
} from '../api/hooks'
import { useAuth } from '../auth/useAuth'
import { CrudTable, type CrudField, type FormValues } from '../components/CrudTable'
import { PageHeader } from '../components/PageHeader'
import { ActiveChip } from '../components/StatusChip'

const mono = { fontFamily: 'monospace' }

/** Empty strings must not reach the API as "" — they mean "not given". */
function clean(values: FormValues): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(values).map(([k, v]) => [k, v === '' ? null : v]),
  )
}

function num(value: unknown): number | null {
  if (value === '' || value === null || value === undefined) return null
  const parsed = Number(value)
  return Number.isNaN(parsed) ? null : parsed
}

/**
 * Every company-scoped master in one place, each tab a full create/edit/delete grid.
 *
 * <p>Deleting a master never erases it: a supplier named on a two-year-old delivery has to
 * keep resolving to a name. It is retired instead, which takes it out of every picker and
 * leaves history readable.</p>
 */
export function ReferencePage() {
  const [tab, setTab] = useState(0)
  const [search, setSearch] = useState('')
  const { can } = useAuth()

  const uoms = useUoms()
  const materialCategories = useMaterialCategories()
  const labour = useLabourCategories()
  const packages = useWorkPackages()
  const materials = useMaterials({ q: search || undefined, page: 0, size: 200 })
  const disciplines = useAllDisciplines()
  const drawingTypes = useAllDrawingTypes()
  const issueCategories = useAllIssueCategories()

  const uomCreate = uomApi.useCreate()
  const uomUpdate = uomApi.useUpdate()
  const uomDelete = uomApi.useDelete()

  const catCreate = materialCategoryApi.useCreate()
  const catUpdate = materialCategoryApi.useUpdate()
  const catDelete = materialCategoryApi.useDelete()

  const labourCreate = labourCategoryApi.useCreate()
  const labourUpdate = labourCategoryApi.useUpdate()
  const labourDelete = labourCategoryApi.useDelete()

  const pkgCreate = workPackageApi.useCreate()
  const pkgUpdate = workPackageApi.useUpdate()
  const pkgDelete = workPackageApi.useDelete()

  const matCreate = materialApi.useCreate()
  const matUpdate = materialApi.useUpdate()
  const matDelete = materialApi.useDelete()

  const discCreate = disciplineApi.useCreate()
  const discUpdate = disciplineApi.useUpdate()
  const discDelete = disciplineApi.useDelete()

  const typeCreate = drawingTypeApi.useCreate()
  const typeUpdate = drawingTypeApi.useUpdate()
  const typeDelete = drawingTypeApi.useDelete()

  const issueCreate = issueCategoryApi.useCreate()
  const issueUpdate = issueCategoryApi.useUpdate()
  const issueDelete = issueCategoryApi.useDelete()

  const canMaterial = can('MASTER_MATERIAL:UPDATE')
  const canLabour = can('MASTER_LABOUR:UPDATE')
  const canPackage = can('MASTER_WORKPKG:UPDATE')

  const uomFields: CrudField[] = [
    { name: 'code', label: 'Code', required: true, lockedOnEdit: true, helperText: 'e.g. BAG, CUM, KG' },
    { name: 'name', label: 'Name', required: true },
    { name: 'decimalPlaces', label: 'Decimal places', type: 'number', defaultValue: 2 },
    { name: 'active', label: 'Active', type: 'switch', defaultValue: true },
  ]

  const categoryFields: CrudField[] = [
    { name: 'code', label: 'Code', required: true, lockedOnEdit: true },
    { name: 'name', label: 'Name', required: true },
    {
      name: 'parentId',
      label: 'Parent category',
      type: 'select',
      options: (materialCategories.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    },
    { name: 'sortOrder', label: 'Sort order', type: 'number', defaultValue: 0 },
    { name: 'active', label: 'Active', type: 'switch', defaultValue: true },
  ]

  const labourFields: CrudField[] = [
    { name: 'code', label: 'Code', required: true, lockedOnEdit: true, helperText: 'e.g. MASON, PLUMBER' },
    { name: 'name', label: 'Category name', required: true },
    { name: 'trade', label: 'Trade' },
    {
      name: 'skillLevel',
      label: 'Skill level',
      type: 'select',
      required: true,
      defaultValue: 'SKILLED',
      options: [
        { value: 'SKILLED', label: 'Skilled' },
        { value: 'SEMI_SKILLED', label: 'Semi-skilled' },
        { value: 'UNSKILLED', label: 'Unskilled' },
      ],
    },
    { name: 'defaultWorkingHours', label: 'Default working hours', type: 'number', defaultValue: 8 },
    { name: 'sortOrder', label: 'Sort order', type: 'number', defaultValue: 0 },
    { name: 'active', label: 'Active', type: 'switch', defaultValue: true },
  ]

  const packageFields: CrudField[] = [
    { name: 'code', label: 'Code', required: true, lockedOnEdit: true },
    { name: 'name', label: 'Work package', required: true },
    { name: 'sortOrder', label: 'Sort order', type: 'number', defaultValue: 0 },
    { name: 'active', label: 'Active', type: 'switch', defaultValue: true },
  ]

  const materialFields: CrudField[] = [
    { name: 'code', label: 'Code', required: true, lockedOnEdit: true },
    { name: 'name', label: 'Material', required: true },
    {
      name: 'categoryId',
      label: 'Category',
      type: 'select',
      required: true,
      options: (materialCategories.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    },
    {
      name: 'uomId',
      label: 'Unit',
      type: 'select',
      required: true,
      options: (uoms.data ?? []).map((u) => ({ value: u.id, label: `${u.code} - ${u.name}` })),
    },
    { name: 'brand', label: 'Brand' },
    { name: 'specification', label: 'Specification', type: 'multiline' },
    { name: 'minStock', label: 'Minimum stock', type: 'number' },
    { name: 'maxStock', label: 'Maximum stock', type: 'number' },
  ]

  const optionFields: CrudField[] = [
    { name: 'code', label: 'Code', required: true, lockedOnEdit: true },
    { name: 'name', label: 'Name', required: true },
    { name: 'sortOrder', label: 'Sort order', type: 'number', defaultValue: 0 },
    { name: 'active', label: 'Active', type: 'switch', defaultValue: true },
  ]

  const issueFields: CrudField[] = [
    ...optionFields.slice(0, 2),
    {
      name: 'defaultPriority',
      label: 'Default priority',
      type: 'select',
      defaultValue: 'MEDIUM',
      options: [
        { value: 'CRITICAL', label: 'Critical' },
        { value: 'HIGH', label: 'High' },
        { value: 'MEDIUM', label: 'Medium' },
        { value: 'LOW', label: 'Low' },
      ],
    },
    ...optionFields.slice(2),
  ]

  const tabs = [
    {
      label: `Units (${uoms.data?.length ?? 0})`,
      node: (
        <CrudTable
          query={uoms}
          rows={uoms.data ?? []}
          addLabel="Add unit"
          describeRow={(u) => u.name}
          columns={[
            { label: 'Code', render: (u) => <span style={mono}>{u.code}</span> },
            { label: 'Name', render: (u) => u.name },
            { label: 'Decimals', render: (u) => u.decimalPlaces },
            { label: 'Active', render: (u) => <ActiveChip active={u.active} /> },
          ]}
          fields={uomFields}
          onCreate={canMaterial ? (v) => uomCreate.mutateAsync({ ...clean(v), decimalPlaces: num(v.decimalPlaces) ?? 2 }) : undefined}
          onUpdate={canMaterial ? (row, v) => uomUpdate.mutateAsync({ id: row.id, body: { ...clean(v), decimalPlaces: num(v.decimalPlaces) ?? 2 } }) : undefined}
          onDelete={canMaterial ? (row) => uomDelete.mutateAsync(row.id) : undefined}
          deleteNote="The unit stops appearing in pickers. Materials already measured in it keep it."
        />
      ),
    },
    {
      label: `Material categories (${materialCategories.data?.length ?? 0})`,
      node: (
        <CrudTable
          query={materialCategories}
          rows={materialCategories.data ?? []}
          addLabel="Add category"
          describeRow={(c) => c.name}
          columns={[
            { label: 'Code', render: (c) => <span style={mono}>{c.code}</span> },
            { label: 'Name', render: (c) => c.name },
            { label: 'Sort order', render: (c) => c.sortOrder },
            { label: 'Active', render: (c) => <ActiveChip active={c.active} /> },
          ]}
          fields={categoryFields}
          onCreate={canMaterial ? (v) => catCreate.mutateAsync({ ...clean(v), sortOrder: num(v.sortOrder) ?? 0 }) : undefined}
          onUpdate={canMaterial ? (row, v) => catUpdate.mutateAsync({ id: row.id, body: { ...clean(v), sortOrder: num(v.sortOrder) ?? 0 } }) : undefined}
          onDelete={canMaterial ? (row) => catDelete.mutateAsync(row.id) : undefined}
        />
      ),
    },
    {
      label: `Labour categories (${labour.data?.length ?? 0})`,
      node: (
        <CrudTable
          query={labour}
          rows={labour.data ?? []}
          addLabel="Add labour category"
          describeRow={(l) => l.name}
          columns={[
            { label: 'Code', render: (l) => <span style={mono}>{l.code}</span> },
            { label: 'Category', render: (l) => l.name },
            { label: 'Trade', render: (l) => l.trade ?? '-' },
            {
              label: 'Skill level',
              render: (l) => (
                <Chip
                  size="small"
                  label={l.skillLevel.replace('_', ' ').toLowerCase()}
                  sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                />
              ),
            },
            { label: 'Default hours', render: (l) => l.defaultWorkingHours },
            { label: 'Active', render: (l) => <ActiveChip active={l.active} /> },
          ]}
          fields={labourFields}
          onCreate={canLabour ? (v) => labourCreate.mutateAsync({
            ...clean(v),
            defaultWorkingHours: num(v.defaultWorkingHours) ?? 8,
            sortOrder: num(v.sortOrder) ?? 0,
          }) : undefined}
          onUpdate={canLabour ? (row, v) => labourUpdate.mutateAsync({
            id: row.id,
            body: {
              ...clean(v),
              defaultWorkingHours: num(v.defaultWorkingHours) ?? 8,
              sortOrder: num(v.sortOrder) ?? 0,
            },
          }) : undefined}
          onDelete={canLabour ? (row) => labourDelete.mutateAsync(row.id) : undefined}
        />
      ),
    },
    {
      label: `Work packages (${packages.data?.length ?? 0})`,
      node: (
        <CrudTable
          query={packages}
          rows={packages.data ?? []}
          addLabel="Add work package"
          describeRow={(w) => w.name}
          columns={[
            { label: 'Code', render: (w) => <span style={mono}>{w.code}</span> },
            { label: 'Work package', render: (w) => w.name },
            { label: 'Scope', render: (w) => (w.global ? 'Company-wide' : 'Project-specific') },
            { label: 'Sort order', render: (w) => w.sortOrder },
            { label: 'Active', render: (w) => <ActiveChip active={w.active} /> },
          ]}
          fields={packageFields}
          onCreate={canPackage ? (v) => pkgCreate.mutateAsync({ ...clean(v), sortOrder: num(v.sortOrder) ?? 0 }) : undefined}
          onUpdate={canPackage ? (row, v) => pkgUpdate.mutateAsync({ id: row.id, body: { ...clean(v), sortOrder: num(v.sortOrder) ?? 0 } }) : undefined}
          onDelete={canPackage ? (row) => pkgDelete.mutateAsync(row.id) : undefined}
          deleteNote="Work packages carrying BOQ items are deactivated rather than removed, so those items keep their heading."
        />
      ),
    },
    {
      label: `Materials (${materials.data?.page.totalElements ?? 0})`,
      node: (
        <CrudTable
          query={materials}
          rows={materials.data?.content ?? []}
          addLabel="Add material"
          describeRow={(m) => m.name}
          toolbar={
            <TextField
              size="small"
              label="Search material, code or brand"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 320 }}
            />
          }
          columns={[
            { label: 'Code', render: (m) => <span style={mono}>{m.code}</span> },
            { label: 'Material', render: (m) => m.name },
            { label: 'Category', render: (m) => m.categoryName ?? '-' },
            { label: 'Unit', render: (m) => m.uomCode ?? '-' },
            { label: 'Brand', render: (m) => m.brand ?? '-' },
            { label: 'Min stock', render: (m) => m.minStock ?? '-', align: 'right' },
            { label: 'Max stock', render: (m) => m.maxStock ?? '-', align: 'right' },
            { label: 'Active', render: (m) => <ActiveChip active={m.active} /> },
          ]}
          fields={materialFields}
          onCreate={canMaterial ? (v) => matCreate.mutateAsync({
            ...clean(v),
            minStock: num(v.minStock),
            maxStock: num(v.maxStock),
          }) : undefined}
          onUpdate={canMaterial ? (row, v) => matUpdate.mutateAsync({
            id: row.id,
            body: { ...clean(v), minStock: num(v.minStock), maxStock: num(v.maxStock) },
          }) : undefined}
          onDelete={canMaterial ? (row) => matDelete.mutateAsync(row.id) : undefined}
          deleteNote="Past receipts and stock keep pointing at this material; it just leaves the pickers."
        />
      ),
    },
    {
      label: 'Suppliers',
      node: <PartyTab type="suppliers" />,
    },
    {
      label: 'Contractors',
      node: <PartyTab type="contractors" />,
    },
    {
      label: 'Consultants',
      node: <PartyTab type="consultants" />,
    },
    {
      label: 'Clients',
      node: <PartyTab type="clients" />,
    },
    {
      label: `Disciplines (${disciplines.data?.length ?? 0})`,
      node: (
        <CrudTable
          query={disciplines}
          rows={disciplines.data ?? []}
          addLabel="Add discipline"
          describeRow={(d) => d.name}
          columns={[
            { label: 'Code', render: (d) => <span style={mono}>{d.code}</span> },
            { label: 'Discipline', render: (d) => d.name },
            { label: 'Sort order', render: (d) => d.sortOrder },
            { label: 'Active', render: (d) => <ActiveChip active={d.active} /> },
          ]}
          fields={optionFields}
          onCreate={canPackage ? (v) => discCreate.mutateAsync({ ...clean(v), sortOrder: num(v.sortOrder) ?? 0 }) : undefined}
          onUpdate={canPackage ? (row, v) => discUpdate.mutateAsync({ id: row.id, body: { ...clean(v), sortOrder: num(v.sortOrder) ?? 0 } }) : undefined}
          onDelete={canPackage ? (row) => discDelete.mutateAsync(row.id) : undefined}
        />
      ),
    },
    {
      label: `Drawing types (${drawingTypes.data?.length ?? 0})`,
      node: (
        <CrudTable
          query={drawingTypes}
          rows={drawingTypes.data ?? []}
          addLabel="Add drawing type"
          describeRow={(t) => t.name}
          columns={[
            { label: 'Code', render: (t) => <span style={mono}>{t.code}</span> },
            { label: 'Drawing type', render: (t) => t.name },
            { label: 'Sort order', render: (t) => t.sortOrder },
            { label: 'Active', render: (t) => <ActiveChip active={t.active} /> },
          ]}
          fields={optionFields}
          onCreate={canPackage ? (v) => typeCreate.mutateAsync({ ...clean(v), sortOrder: num(v.sortOrder) ?? 0 }) : undefined}
          onUpdate={canPackage ? (row, v) => typeUpdate.mutateAsync({ id: row.id, body: { ...clean(v), sortOrder: num(v.sortOrder) ?? 0 } }) : undefined}
          onDelete={canPackage ? (row) => typeDelete.mutateAsync(row.id) : undefined}
        />
      ),
    },
    {
      label: `Issue categories (${issueCategories.data?.length ?? 0})`,
      node: (
        <CrudTable
          query={issueCategories}
          rows={issueCategories.data ?? []}
          addLabel="Add issue category"
          describeRow={(c) => c.name}
          columns={[
            { label: 'Code', render: (c) => <span style={mono}>{c.code}</span> },
            { label: 'Issue category', render: (c) => c.name },
            { label: 'Default priority', render: (c) => c.defaultPriority ?? '-' },
            { label: 'Sort order', render: (c) => c.sortOrder },
            { label: 'Active', render: (c) => <ActiveChip active={c.active} /> },
          ]}
          fields={issueFields}
          onCreate={canPackage ? (v) => issueCreate.mutateAsync({ ...clean(v), sortOrder: num(v.sortOrder) ?? 0 }) : undefined}
          onUpdate={canPackage ? (row, v) => issueUpdate.mutateAsync({ id: row.id, body: { ...clean(v), sortOrder: num(v.sortOrder) ?? 0 } }) : undefined}
          onDelete={canPackage ? (row) => issueDelete.mutateAsync(row.id) : undefined}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Reference data"
        subtitle="Every master the daily forms pick from. Add, edit and retire them here."
      />

      <Paper variant="outlined">
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {tabs.map((t) => (
            <Tab key={t.label} label={t.label} />
          ))}
        </Tabs>
        <Box>{tabs[tab]?.node}</Box>
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Codes are identity and cannot be changed once created. Deleting retires a row: it
        leaves the pickers, and everything already recorded against it stays intact.
      </Typography>
    </>
  )
}

const PARTY_LABEL: Record<PartyType, { one: string; many: string }> = {
  suppliers: { one: 'supplier', many: 'Suppliers' },
  contractors: { one: 'contractor', many: 'Contractors' },
  consultants: { one: 'consultant', many: 'Consultants' },
  clients: { one: 'client', many: 'Clients' },
}

/** The four party masters share a shape, so they share a tab body. */
function PartyTab({ type }: { type: PartyType }) {
  const { can } = useAuth()
  const query = useParties(type)
  const create = useCreateParty(type)
  const update = useUpdateParty(type)
  const remove = useDeleteParty(type)
  const label = PARTY_LABEL[type]

  const canCreate = can('PARTY:CREATE')
  const canUpdate = can('PARTY:UPDATE')
  const canDelete = can('PARTY:DELETE')

  const fields: CrudField[] = [
    { name: 'code', label: 'Code', required: true, lockedOnEdit: true },
    { name: 'name', label: 'Name', required: true },
    { name: 'contactPerson', label: 'Contact person' },
    { name: 'mobile', label: 'Mobile' },
    { name: 'email', label: 'Email' },
    { name: 'address', label: 'Address', type: 'multiline' },
    ...(type === 'consultants'
      ? ([
          { name: 'discipline', label: 'Discipline', helperText: 'Structural, MEP, kitchen, architecture' },
          { name: 'canIssueDrawings', label: 'Issues drawings', type: 'switch', defaultValue: true },
        ] as CrudField[])
      : []),
    ...(type === 'contractors'
      ? ([
          { name: 'trade', label: 'Trade' },
          { name: 'suppliesLabour', label: 'Supplies labour', type: 'switch', defaultValue: true },
        ] as CrudField[])
      : []),
    ...(type === 'suppliers' ? ([{ name: 'rating', label: 'Rating (1-5)', type: 'number' }] as CrudField[]) : []),
    ...(type !== 'consultants' ? ([{ name: 'gstNo', label: 'GST number' }] as CrudField[]) : []),
    { name: 'active', label: 'Active', type: 'switch', defaultValue: true },
  ]

  function body(values: FormValues): PartySaveRequest {
    return {
      ...(clean(values) as unknown as PartySaveRequest),
      rating: num(values.rating),
    }
  }

  return (
    <CrudTable
      query={query}
      rows={query.data ?? []}
      addLabel={`Add ${label.one}`}
      describeRow={(p) => p.name}
      columns={[
        { label: 'Code', render: (p) => <span style={mono}>{p.code}</span> },
        { label: label.many.replace(/s$/, ''), render: (p) => p.name },
        ...(type === 'consultants'
          ? [{ label: 'Discipline', render: (p: { discipline?: string }) => p.discipline ?? '-' }]
          : []),
        ...(type === 'contractors'
          ? [{ label: 'Trade', render: (p: { trade?: string }) => p.trade ?? '-' }]
          : []),
        ...(type === 'suppliers'
          ? [{ label: 'Rating', render: (p: { rating?: number }) => p.rating ?? '-' }]
          : []),
        { label: 'Contact', render: (p) => p.contactPerson ?? '-' },
        { label: 'Mobile', render: (p) => p.mobile ?? '-' },
        { label: 'Active', render: (p) => <ActiveChip active={p.active} /> },
      ]}
      fields={fields}
      onCreate={canCreate ? (v) => create.mutateAsync(body(v)) : undefined}
      onUpdate={canUpdate ? (row, v) => update.mutateAsync({ id: row.id, body: body(v) }) : undefined}
      onDelete={canDelete ? (row) => remove.mutateAsync(row.id) : undefined}
      deleteNote={`Deliveries, drawings and reports naming this ${label.one} keep the name. It only leaves the pickers.`}
      emptyMessage={`No ${label.many.toLowerCase()} yet.`}
    />
  )
}
